import { nullish } from 'Lib/type-sanity';

/**
 * The `Debounce` class is just a wrapper around `setTimeout`/`clearTimeout` calls
 * in order to make working with debounces less verbose. The debounce won't start
 * until either `start` or `restart` are called.
 */
export default class Debounce {
  /**
   * @type { boolean }
   */
  #running = false;
  /**
   * @type { number }
   */
  wait_time;

  /**
   * @type { number }
   */
  timeout_handle;

  /**
   * @type { Function }
   */
  callback;

  /**
   * @type { boolean }
   */
  get running() {
    return this.#running;
  }

  /**
   *
   * @param { number } wait_time
   * @param { Function } callback
   */
  constructor(wait_time, callback) {
    this.wait_time = wait_time;
    this.callback = callback;
  }

  execute() {
    this.stop();
    this.callback();
  }

  /**
   * Stops the timer if running, then starts it again.
   * Internally just calls `stop` then `start`
   * Optionally, accepts an object with optional parameters to reassign
   * `wait_time` and/or `callback`
   *
   * @param { Object } [options]
   * @param { number } [options.wait_time]
   * @param { Function | undefined } [options.callback]
   */
  restart(options) {
    if (!nullish(options)) {
      if (!nullish(options.wait_time)) this.wait_time = options.wait_time;
      if (!nullish(options.callback)) this.callback = options.callback;
    }
    this.stop();
    this.start();
  }

  /**
   * If the timer is not already running, starts a new one
   */
  start() {
    if (!this.#running) {
      this.timeout_handle = setTimeout(() => this.execute(), this.wait_time);
    }
    this.#running = true;
  }

  /**
   * Stops and clears the timer, if running.
   */
  stop() {
    clearTimeout(this.timeout_handle);
    this.#running = false;
  }
}
